import {
  PAYMENT_REQUEST_STATUS_LABEL,
  PAYMENT_REQUEST_STATUS_TONE,
  canCancelPayment,
  formatPaymentAmount,
  paymentStatusOf,
  type PaymentRequestRow,
} from '@/domain/payments';
import { cn } from '@/lib/cn';
import { Badge, Button } from '@/ui/common';

export interface RecentPaymentRowsProps {
  rows: PaymentRequestRow[];
  currency?: string;
  title?: string;
  emptyLabel?: string;
  cancelLabel?: string;
  onCancel?: (encId: string) => void;
  cancelling?: boolean;
  className?: string;
}

function formatDate(value: string | null | undefined): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function RecentPaymentRows({
  rows,
  currency = '',
  title = 'Recent',
  emptyLabel = 'No recent requests.',
  cancelLabel = 'Cancel',
  onCancel,
  cancelling = false,
  className,
}: RecentPaymentRowsProps) {
  return (
    <section className={cn('flex flex-col gap-2', className)}>
      <h2 className="text-[13px] font-bold text-text-secondary uppercase tracking-wide">
        {title}
      </h2>
      {rows.length === 0 ? (
        <p className="text-[13px] text-text-secondary">{emptyLabel}</p>
      ) : (
        <div className="flex flex-col gap-2">
          {rows.slice(0, 5).map((row) => {
            const status = paymentStatusOf(row.Status);
            const encId = row.EncId?.trim() ?? '';
            return (
              <article
                key={encId || `${row.Type}-${row.Date}-${row.Amount}`}
                className="flex items-center gap-3 rounded-sm border border-border-subtle bg-bg-surface px-4 py-3"
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[14px] font-bold text-text-primary">
                    {row.Type || '—'}
                  </p>
                  <p className="text-[11px] text-text-secondary">{formatDate(row.Date)}</p>
                </div>
                <div className="flex shrink-0 flex-col items-end gap-1">
                  <p className="text-[14px] font-bold text-text-primary">
                    {formatPaymentAmount(row.Amount, currency)}
                  </p>
                  <Badge tone={PAYMENT_REQUEST_STATUS_TONE[status]} size="sm">
                    {PAYMENT_REQUEST_STATUS_LABEL[status]}
                  </Badge>
                  {onCancel && canCancelPayment(row.Status) && encId ? (
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      loading={cancelling}
                      onClick={() => onCancel(encId)}
                    >
                      {cancelLabel}
                    </Button>
                  ) : null}
                </div>
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
}
